import { useState, type Ref } from 'react';
import { Skeleton, Separator } from '@admin-panel/ui';
import type { LookDTOType, LookGroupDTOType } from '../../../../schemas/styling';
import { formatWorkflowLine, getRenderableLooks } from '../../helpers';
import LookGroupWorkspaceHeader from './LookGroupWorkspaceHeader';
import LookGroupWorkspaceRow from './LookGroupWorkspaceRow';
import { toGroupLookSummary } from './utils';

export type LookGroupWorkspaceProps = {
  summaryGroup: LookGroupDTOType;
  group: LookGroupDTOType;
  isLoadingDetails?: boolean;
  elementRef?: Ref<HTMLDivElement>;
};

const LookGroupWorkspace = ({
  summaryGroup,
  group,
  isLoadingDetails = false,
  elementRef,
}: LookGroupWorkspaceProps) => {
  const [optimisticGroup, setOptimisticGroup] = useState(group);
  const workflowLine = formatWorkflowLine(summaryGroup);
  const looks = getRenderableLooks(optimisticGroup);

  const handleLookUpdated = (updatedLook: LookDTOType) => {
    const nextLookSummary = toGroupLookSummary(updatedLook);

    setOptimisticGroup(previousGroup => {
      const previousLooks = previousGroup.looks ?? [];
      const nextLooks = previousLooks.some(look => look.id === updatedLook.id)
        ? previousLooks.map(look =>
            look.id === updatedLook.id ? { ...look, ...nextLookSummary } : look,
          )
        : [nextLookSummary, ...previousLooks];

      const shouldUpdatePrimaryLook =
        previousGroup.primary_look_id === updatedLook.id ||
        previousGroup.primary_look?.id === updatedLook.id;

      return {
        ...previousGroup,
        looks: nextLooks,
        primary_look: shouldUpdatePrimaryLook ? nextLookSummary : previousGroup.primary_look,
      };
    });
  };

  return (
    <article
      ref={elementRef}
      className="relative overflow-clip rounded-sm border border-border/60 bg-background"
    >
      <LookGroupWorkspaceHeader
        workflowLine={workflowLine}
        brandName={optimisticGroup.base_product_brand_name || summaryGroup.base_product_brand_name}
        productName={optimisticGroup.base_product_name || summaryGroup.base_product_name}
        productId={optimisticGroup.base_product_id || summaryGroup.base_product_id}
        productColor={optimisticGroup.base_product_color || summaryGroup.base_product_color}
      />

      <div className="p-3">
        {isLoadingDetails && !(optimisticGroup.looks?.length ?? 0) ? (
          <div className="space-y-3">
            <Skeleton className="h-64 w-full rounded-sm" />
            <Skeleton className="h-64 w-full rounded-sm" />
          </div>
        ) : looks.length > 0 ? (
          <div className="space-y-6">
            {looks.map((look, index) => (
              <div key={look.id}>
                <LookGroupWorkspaceRow
                  group={optimisticGroup}
                  look={look}
                  workflowLine={workflowLine}
                  onLookUpdated={handleLookUpdated}
                />
                {index !== looks.length - 1 && (
                  <Separator variant="dashed" className="my-4" size="md" />
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex min-h-88 items-center justify-center text-sm text-muted-foreground">
            No looks available to inspect.
          </div>
        )}
      </div>
    </article>
  );
};

export default LookGroupWorkspace;
